import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "./hooks";
import scrollToMessage from "./scrollToMessage";
import { updateNavigateToSpesificMessage } from "../store/Slices/NavigateToSpesificMessageSlice";
import { showAppError } from "./showAppError";
import { fetchChatMessages } from "./fetchChatMessages";
import { replaceMessageWindow } from "../store/Slices/MessageListSlice";
import { updateMessageOptions } from "../store/Slices/MessageOptionsSlice";
import { messageListPayloadChatId } from "./socketQueries";

export default function NavigateToSpesificMessage() {
  const NavigateToSpesificMessageData = useAppSelector(
    (state) => state.NavigateToSpesificMessage,
  );
  const MessageListArray = useAppSelector((state) => state.MessageList);
  const currentConversation = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const dispatch = useAppDispatch();
  const fetchingRef = useRef(false);
  const activeChatRef = useRef(currentConversation.chat_id);
  activeChatRef.current = currentConversation.chat_id;

  useEffect(() => {
    if (!NavigateToSpesificMessageData.navigate_to_message) {
      fetchingRef.current = false;
      return;
    }

    const messageId = NavigateToSpesificMessageData.message_id;
    const chatId =
      NavigateToSpesificMessageData.chat_id || currentConversation.chat_id;

    const alreadyLoaded = MessageListArray.message_list.Records.some(
      (message) => message.message_id == messageId,
    );

    if (alreadyLoaded) {
      setTimeout(() => {
        scrollToMessage(messageId as number, true, true, "center");
      }, 80);
      dispatch(
        updateNavigateToSpesificMessage({
          chat_id: 0,
          navigate_to_message: false,
          message_id: 0,
        }),
      );
      return;
    }

    if (fetchingRef.current || !chatId || !messageId) return;
    fetchingRef.current = true;

    (async () => {
      try {
        dispatch(
          updateMessageOptions({
            isMoreMessageLoading: true,
          }),
        );

        const res = await fetchChatMessages({
          chat_id: chatId,
          user_id: currentConversation.user_id,
          around_message_id: messageId as number,
          before: 35,
          after: 25,
          // Pin bar uses get_pinned_messages — not around window
          pinned: false,
        });

        // User may have switched chats while the jump was in flight
        if (Number(activeChatRef.current) !== Number(chatId)) return;

        const payloadChatId = messageListPayloadChatId(res);
        if (payloadChatId != null && Number(payloadChatId) !== Number(chatId)) {
          return;
        }

        const pagination = res?.message_list?.Pagination;
        const found =
          pagination?.anchor_found !== false &&
          res?.message_list?.Records?.some(
            (m) => m.message_id == messageId,
          );

        if (!found) {
          showAppError({
            title: "Message not found",
            message: "Закреплённое сообщение не найдено в этом чате.",
            details: JSON.stringify(
              {
                message_id: messageId,
                chat_id: chatId,
                anchor_found: pagination?.anchor_found,
                returned: res?.message_list?.Records?.length ?? 0,
              },
              null,
              2,
            ),
          });
          return;
        }

        dispatch(replaceMessageWindow(res));
        dispatch(
          updateMessageOptions({
            historyMode: "window",
            has_more_older: !!pagination?.has_more_older,
            has_more_newer: !!pagination?.has_more_newer,
            isMoreMessageLoading: false,
            isMessageLoading: false,
            messageListAtTop: false,
            // Keep InfiniteScroll from page-mode fetching
            totalPages: pagination?.has_more_older ? 999 : 1,
            currentPage: "1",
          }),
        );

        setTimeout(() => {
          scrollToMessage(messageId as number, true, true, "center");
        }, 120);
      } catch (err) {
        showAppError(err);
      } finally {
        fetchingRef.current = false;
        dispatch(
          updateMessageOptions({
            isMoreMessageLoading: false,
          }),
        );
        dispatch(
          updateNavigateToSpesificMessage({
            chat_id: 0,
            navigate_to_message: false,
            message_id: 0,
          }),
        );
      }
    })();
  }, [
    NavigateToSpesificMessageData.navigate_to_message,
    NavigateToSpesificMessageData.message_id,
    MessageListArray.message_list.Records,
    currentConversation.chat_id,
    currentConversation.user_id,
    dispatch,
  ]);

  return <div />;
}
